'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckoutAddressTypeEnum } from '@sola/schema/dotcom/checkout/contactInfo.ts';
import { BillingAddressEnum, MailingAddressEnum } from '@sola/schema/dotcom/shared/address.ts';
import { COUNTRY_CODE_US, CountryCode, CountryEnum, StateEnum } from '@sola/schema/dotcom/shared/geo.ts';
import { InputField } from '../input-field/input-field.tsx';
import { PostalCodeField } from '../postal-code-field/postal-code-field.tsx';
import { Select } from '../select/select.tsx';
import { StateField } from '../state-field/state-field.tsx';
const stateOptions = Object.entries(StateEnum).map(([abbr, name]) => ({
  text: name,
  value: abbr
}));
const countryOptions = Object.entries(CountryEnum).map(([abbr, name]) => ({
  text: name,
  value: abbr
}));
export interface AddressFieldsProps {
  addressType: CheckoutAddressTypeEnum;
  /**
   * Used only when country needs to be controlled.
   */
  countryValue?: CountryCode;
  defaultCountry?: CountryCode;
  onCountryChange?: (countryCode: CountryCode) => void;
  shouldUnregister?: boolean;
  shouldValidate?: boolean;
}
export const AddressFields: React.FC<AddressFieldsProps> = t0 => {
  const $ = _c(102);
  const {
    addressType,
    countryValue,
    defaultCountry,
    onCountryChange,
    shouldUnregister,
    shouldValidate: t1
  } = t0;
  const shouldValidate = t1 === undefined ? true : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["checkout", "forms"];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t
  } = useTranslation(t2);
  const [countryState, setCountryState] = useState(defaultCountry);
  const selectedCountry = countryValue || countryState;
  const addressLabelToken = `${addressType}AddressLabel`;
  const autoCompleteGroup = addressType === CheckoutAddressTypeEnum.Mailing ? "" : "billing ";
  const FieldGroupEnum = addressType === CheckoutAddressTypeEnum.Mailing ? MailingAddressEnum : BillingAddressEnum;
  const isUS = selectedCountry === COUNTRY_CODE_US;
  const t3 = `forms:addressFields.${addressLabelToken}`;
  let t4;
  if ($[1] !== t || $[2] !== t3) {
    t4 = t(t3);
    $[1] = t;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = FieldGroupEnum.Address;
  const t6 = `${autoCompleteGroup}address-line1`;
  let t7;
  if ($[4] !== t) {
    t7 = t("forms:errors.addressNumberError");
    $[4] = t;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] !== t7) {
    t8 = {
      message: t7
    };
    $[6] = t7;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = /^$|^(?![\w!#$%&'*+/=?^_`{|}~.-]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.([a-zA-Z]{2,63}))/;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] !== t) {
    t10 = t("forms:errors.addressNumberError");
    $[9] = t;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] !== t10) {
    t11 = {
      value: t9,
      message: t10
    };
    $[11] = t10;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  const t12 = shouldUnregister ? true : undefined;
  let t13;
  if ($[13] !== FieldGroupEnum.Address || $[14] !== shouldValidate || $[15] !== t11 || $[16] !== t12 || $[17] !== t4 || $[18] !== t6 || $[19] !== t8) {
    t13 = <InputField label={t4} name={t5} autoComplete={t6} required={shouldValidate} removeCardNumbers={t8} pattern={t11} shouldUnregister={t12} />;
    $[13] = FieldGroupEnum.Address;
    $[14] = shouldValidate;
    $[15] = t11;
    $[16] = t12;
    $[17] = t4;
    $[18] = t6;
    $[19] = t8;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== t) {
    t14 = t("forms:addressFields.addressAdditionalLabel");
    $[21] = t;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  const t15 = FieldGroupEnum.Address2;
  const t16 = `${autoCompleteGroup}address-line2`;
  let t17;
  if ($[23] !== t) {
    t17 = t("forms:errors.addressNumberError");
    $[23] = t;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  if ($[25] !== t17) {
    t18 = {
      message: t17
    };
    $[25] = t17;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  const t19 = shouldUnregister ? true : undefined;
  let t20;
  if ($[27] !== FieldGroupEnum.Address2 || $[28] !== t14 || $[29] !== t16 || $[30] !== t18 || $[31] !== t19) {
    t20 = <InputField label={t14} name={t15} autoComplete={t16} removeCardNumbers={t18} shouldUnregister={t19} />;
    $[27] = FieldGroupEnum.Address2;
    $[28] = t14;
    $[29] = t16;
    $[30] = t18;
    $[31] = t19;
    $[32] = t20;
  } else {
    t20 = $[32];
  }
  let t21;
  if ($[33] !== t) {
    t21 = t("forms:addressFields.countryLabel");
    $[33] = t;
    $[34] = t21;
  } else {
    t21 = $[34];
  }
  let t22;
  if ($[35] !== t) {
    t22 = t("forms:addressFields.countryPlaceholder");
    $[35] = t;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  const t23 = `${autoCompleteGroup}country`;
  const t24 = shouldUnregister ? true : undefined;
  let t25;
  if ($[37] !== onCountryChange) {
    t25 = e => {
      const {
        value
      } = e.target;
      if (onCountryChange) {
        onCountryChange(value);
      } else {
        setCountryState(value);
      }
    };
    $[37] = onCountryChange;
    $[38] = t25;
  } else {
    t25 = $[38];
  }
  let t26;
  if ($[39] !== countryValue) {
    t26 = countryValue && {
      value: countryValue
    };
    $[39] = countryValue;
    $[40] = t26;
  } else {
    t26 = $[40];
  }
  let t27;
  if ($[41] !== FieldGroupEnum.Country || $[42] !== defaultCountry || $[43] !== shouldValidate || $[44] !== t21 || $[45] !== t22 || $[46] !== t23 || $[47] !== t24 || $[48] !== t25 || $[49] !== t26) {
    t27 = <Select label={t21} placeholder={t22} defaultValue={defaultCountry} name={FieldGroupEnum.Country} autoComplete={t23} required={shouldValidate} items={countryOptions} shouldUnregister={t24} onChange={t25} {...t26} />;
    $[41] = FieldGroupEnum.Country;
    $[42] = defaultCountry;
    $[43] = shouldValidate;
    $[44] = t21;
    $[45] = t22;
    $[46] = t23;
    $[47] = t24;
    $[48] = t25;
    $[49] = t26;
    $[50] = t27;
  } else {
    t27 = $[50];
  }
  let t28;
  if ($[51] !== t) {
    t28 = t("forms:addressFields.cityLabel");
    $[51] = t;
    $[52] = t28;
  } else {
    t28 = $[52];
  }
  const t29 = FieldGroupEnum.City;
  const t30 = `${autoCompleteGroup}address-level2`;
  let t31;
  if ($[53] !== t) {
    t31 = t("forms:errors.cityNumberError");
    $[53] = t;
    $[54] = t31;
  } else {
    t31 = $[54];
  }
  let t32;
  if ($[55] !== t31) {
    t32 = {
      message: t31
    };
    $[55] = t31;
    $[56] = t32;
  } else {
    t32 = $[56];
  }
  const t33 = shouldUnregister ? true : undefined;
  let t34;
  if ($[57] !== FieldGroupEnum.City || $[58] !== shouldValidate || $[59] !== t28 || $[60] !== t30 || $[61] !== t32 || $[62] !== t33) {
    t34 = <InputField label={t28} name={t29} fieldClasses="flex-1" autoComplete={t30} autoCorrect="off" spellCheck={false} required={shouldValidate} removeCardNumbers={t32} shouldUnregister={t33} />;
    $[57] = FieldGroupEnum.City;
    $[58] = shouldValidate;
    $[59] = t28;
    $[60] = t30;
    $[61] = t32;
    $[62] = t33;
    $[63] = t34;
  } else {
    t34 = $[63];
  }
  let t35;
  if ($[64] !== isUS || $[65] !== t) {
    t35 = isUS ? t("forms:addressFields.stateLabel") : t("forms:addressFields.provinceLabel");
    $[64] = isUS;
    $[65] = t;
    $[66] = t35;
  } else {
    t35 = $[66];
  }
  const t36 = FieldGroupEnum.State;
  const t37 = `${autoCompleteGroup}address-level1`;
  let t38;
  if ($[67] !== t) {
    t38 = t("forms:errors.provinceNumberError");
    $[67] = t;
    $[68] = t38;
  } else {
    t38 = $[68];
  }
  let t39;
  if ($[69] !== t38) {
    t39 = {
      message: t38
    };
    $[69] = t38;
    $[70] = t39;
  } else {
    t39 = $[70];
  }
  let t40;
  if ($[71] !== isUS || $[72] !== t) {
    t40 = isUS ? t("forms:addressFields.statePlaceholder") : "";
    $[71] = isUS;
    $[72] = t;
    $[73] = t40;
  } else {
    t40 = $[73];
  }
  const t41 = shouldValidate && isUS;
  const t42 = shouldUnregister ? true : undefined;
  let t43;
  if ($[74] !== FieldGroupEnum.State || $[75] !== isUS || $[76] !== t35 || $[77] !== t37 || $[78] !== t39 || $[79] !== t40 || $[80] !== t41 || $[81] !== t42) {
    t43 = <StateField label={t35} name={t36} fieldClasses="flex-1" autoComplete={t37} spellCheck={false} removeCardNumbers={t39} placeholder={t40} items={stateOptions} required={t41} shouldUnregister={t42} isUS={isUS} />;
    $[74] = FieldGroupEnum.State;
    $[75] = isUS;
    $[76] = t35;
    $[77] = t37;
    $[78] = t39;
    $[79] = t40;
    $[80] = t41;
    $[81] = t42;
    $[82] = t43;
  } else {
    t43 = $[82];
  }
  let t44;
  if ($[83] !== isUS || $[84] !== t) {
    t44 = isUS ? t("forms:addressFields.zipCodeLabel") : t("forms:addressFields.postalCodeLabel");
    $[83] = isUS;
    $[84] = t;
    $[85] = t44;
  } else {
    t44 = $[85];
  }
  const t45 = `${autoCompleteGroup}postal-code`;
  const t46 = shouldUnregister ? true : undefined;
  let t47;
  if ($[86] !== FieldGroupEnum.Postal || $[87] !== selectedCountry || $[88] !== shouldValidate || $[89] !== t44 || $[90] !== t45 || $[91] !== t46) {
    t47 = <PostalCodeField label={t44} name={FieldGroupEnum.Postal} fieldClasses="flex-1" autoComplete={t45} shouldUnregister={t46} countryCode={selectedCountry} shouldValidate={shouldValidate} />;
    $[86] = FieldGroupEnum.Postal;
    $[87] = selectedCountry;
    $[88] = shouldValidate;
    $[89] = t44;
    $[90] = t45;
    $[91] = t46;
    $[92] = t47;
  } else {
    t47 = $[92];
  }
  let t48;
  if ($[93] !== t34 || $[94] !== t43 || $[95] !== t47) {
    t48 = <div className="flex gap-comfy-sm flex-col lg:flex-row">{t34}{t43}{t47}</div>;
    $[93] = t34;
    $[94] = t43;
    $[95] = t47;
    $[96] = t48;
  } else {
    t48 = $[96];
  }
  let t49;
  if ($[97] !== t13 || $[98] !== t20 || $[99] !== t27 || $[100] !== t48) {
    t49 = <>{t13}{t20}{t27}{t48}</>;
    $[97] = t13;
    $[98] = t20;
    $[99] = t27;
    $[100] = t48;
    $[101] = t49;
  } else {
    t49 = $[101];
  }
  return t49;
};